import React from 'react';
import { StarIcon, UserIcon } from '@heroicons/react/24/solid';
import { Button } from '@theraply/ui';
import { TherapistPublicProfile } from '../../hooks/useTherapistProfile';

interface ProfileHeroProps {
  profile: TherapistPublicProfile;
  onBookAppointment: () => void;
}

/**
 * Hero section for therapist profile page.
 *
 * Features:
 * - Large profile photo with fallback
 * - Name and credentials
 * - Star rating with count
 * - Specialty tags
 * - "Book Appointment" CTA button
 *
 * Accessibility:
 * - ARIA labels for rating
 * - Semantic HTML structure
 * - Keyboard navigation support
 */
export const ProfileHero: React.FC<ProfileHeroProps> = ({ profile, onBookAppointment }) => {
  const {
    firstName,
    lastName,
    profilePhotoS3Key,
    rating,
    reviewCount,
    specialties,
    yearsOfExperience,
    isAcceptingNewClients,
  } = profile;

  const fullName = `${firstName} ${lastName}`;

  // Render star rating
  const renderStars = () => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;

    for (let i = 0; i < 5; i++) {
      if (i < fullStars) {
        stars.push(
          <StarIcon key={i} className="h-6 w-6 text-yellow-400 fill-current" />
        );
      } else if (i === fullStars && hasHalfStar) {
        stars.push(
          <div key={i} className="relative">
            <StarIcon className="h-6 w-6 text-gray-300" />
            <div className="absolute inset-0 overflow-hidden w-1/2">
              <StarIcon className="h-6 w-6 text-yellow-400 fill-current" />
            </div>
          </div>
        );
      } else {
        stars.push(
          <StarIcon key={i} className="h-6 w-6 text-gray-300" />
        );
      }
    }

    return stars;
  };

  return (
    <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex flex-col md:flex-row gap-8 items-center md:items-start">
          {/* Profile Photo */}
          <div className="flex-shrink-0">
            {profilePhotoS3Key ? (
              <img
                src={`${import.meta.env.VITE_S3_BUCKET_URL || 'https://d3example.cloudfront.net'}/${profilePhotoS3Key}`}
                alt={fullName}
                className="h-48 w-48 rounded-full object-cover border-4 border-white shadow-xl"
              />
            ) : (
              <div className="h-48 w-48 rounded-full bg-white/20 backdrop-blur flex items-center justify-center border-4 border-white shadow-xl">
                <UserIcon className="h-24 w-24 text-white" />
              </div>
            )}
          </div>

          {/* Profile Info */}
          <div className="flex-1 text-center md:text-left">
            <h1 className="text-4xl font-bold mb-3">{fullName}</h1>

            {/* Credentials Badge */}
            {yearsOfExperience && (
              <div className="inline-block bg-white/20 backdrop-blur px-4 py-1 rounded-full text-sm font-medium mb-4">
                {yearsOfExperience} years of experience
              </div>
            )}

            {/* Rating */}
            <div className="flex items-center gap-3 mb-4 justify-center md:justify-start">
              <div className="flex items-center" aria-label={`Rating: ${rating.toFixed(1)} out of 5 stars`}>
                {renderStars()}
              </div>
              <span className="text-lg font-medium">
                {rating.toFixed(1)}
              </span>
              <span className="text-white/80">
                ({reviewCount} {reviewCount === 1 ? 'review' : 'reviews'})
              </span>
            </div>

            {/* Specialties */}
            {specialties.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-6 justify-center md:justify-start">
                {specialties.map((specialty) => (
                  <span
                    key={specialty}
                    className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-white/20 backdrop-blur"
                  >
                    {specialty.replace(/_/g, ' ')}
                  </span>
                ))}
              </div>
            )}

            {/* Accepting New Clients Badge */}
            {isAcceptingNewClients && (
              <div className="inline-flex items-center gap-2 bg-green-500 px-4 py-2 rounded-lg text-white font-medium mb-6">
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                Accepting New Clients
              </div>
            )}

            {/* CTA Button */}
            <div className="flex gap-4 justify-center md:justify-start">
              <Button
                onClick={onBookAppointment}
                className="bg-white text-indigo-600 hover:bg-gray-100 px-8 py-3 text-lg font-semibold shadow-lg"
                disabled={!isAcceptingNewClients}
              >
                {isAcceptingNewClients ? 'Book Appointment' : 'Not Accepting Clients'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
